import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { captureMutationError } from '@/shared/lib/sentry-utils';
import { parseApiError } from '@/api/parse-error';
import { queryKeys } from '@/api/query-keys';
import {
  getUsers,
  getUser,
  createUser,
  updateUser,
  deleteUser,
  getUserUsage,
  getUserPreferences,
  updateUserPreferences,
  getUserRiskPolicy,
  updateUserRiskPolicy,
  getUserOnboardingStatus,
  getMyOnboardingStatus,
  getUsersOnboardingSummary,
  backfillProvisioning,
  type UserListParams,
  type UserCreate,
  type UserUpdate,
  type PreferencesUpdate,
  type RiskPolicyUpdate,
} from '@/api/endpoints/users';

export function useUsers(params?: UserListParams) {
  return useQuery({
    queryKey: queryKeys.users.list(params as Record<string, unknown>),
    queryFn: () => getUsers(params),
  });
}

export function useUser(id: string | number) {
  return useQuery({
    queryKey: queryKeys.users.detail(String(id)),
    queryFn: () => getUser(id),
    enabled: !!id,
  });
}

export function useUserUsage(id: string | number) {
  return useQuery({
    queryKey: queryKeys.users.usage(String(id)),
    queryFn: () => getUserUsage(id),
    enabled: !!id,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UserCreate) => createUser(data),
    onSuccess: (user) => {
      void qc.invalidateQueries({ queryKey: queryKeys.users.all() });
      toast.success(`User "${user.name}" created`);
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to create user'));
      captureMutationError(err, { mutation: 'create_user' });
    },
  });
}

export function useUpdateUser(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UserUpdate) => updateUser(id, data),
    onSuccess: (user) => {
      void qc.invalidateQueries({ queryKey: queryKeys.users.all() });
      void qc.invalidateQueries({ queryKey: queryKeys.users.detail(String(id)) });
      toast.success(`User "${user.name}" updated`);
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to update user'));
      captureMutationError(err, { mutation: 'update_user', user_id: id });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => deleteUser(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.users.all() });
      toast.success('User deleted');
    },
    onError: (err, id) => {
      toast.error(parseApiError(err, 'Failed to delete user'));
      captureMutationError(err, { mutation: 'delete_user', user_id: id });
    },
  });
}

export function useUserPreferences(id: string | number) {
  return useQuery({
    queryKey: queryKeys.users.preferences(String(id)),
    queryFn: () => getUserPreferences(id),
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateUserPreferences(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PreferencesUpdate) => updateUserPreferences(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.users.preferences(String(id)) });
      toast.success('User preferences updated');
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to update user preferences'));
      captureMutationError(err, { mutation: 'update_user_preferences', user_id: id });
    },
  });
}

export function useUserRiskPolicy(id: string | number) {
  return useQuery({
    queryKey: queryKeys.users.riskPolicy(String(id)),
    queryFn: () => getUserRiskPolicy(id),
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateUserRiskPolicy(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RiskPolicyUpdate) => updateUserRiskPolicy(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.users.riskPolicy(String(id)) });
      toast.success('User risk policy updated');
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to update user risk policy'));
      captureMutationError(err, { mutation: 'update_user_risk_policy', user_id: id });
    },
  });
}

export function useUserOnboardingStatus(id: string | number) {
  return useQuery({
    queryKey: queryKeys.users.onboardingStatus(String(id)),
    queryFn: () => getUserOnboardingStatus(id),
    enabled: !!id,
  });
}

export function useMyOnboardingStatus() {
  return useQuery({
    queryKey: queryKeys.me.onboardingStatus(),
    queryFn: () => getMyOnboardingStatus(),
  });
}

export function useUsersOnboardingSummary() {
  return useQuery({
    queryKey: queryKeys.users.onboardingSummary(),
    queryFn: () => getUsersOnboardingSummary(),
  });
}

export function useBackfillProvisioning() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => backfillProvisioning(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.users.all() });
      toast.success('Provisioning backfill complete');
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to backfill provisioning'));
      captureMutationError(err, { mutation: 'backfill_provisioning' });
    },
  });
}
